// After a deploy, users with an old tab still reference chunk hashes that
// no longer exist on the server. The next lazy `import()` they trigger throws
// "Failed to fetch dynamically imported module" / "Importing a module script
// failed" / "Loading chunk … failed". We catch that and force a single
// hard reload so the browser picks up the new index.html with the new hashes.

const RELOAD_FLAG = 'intver2:stale-bundle-reload-attempted';

const STALE_BUNDLE_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading chunk \S+ failed/i,
  /Loading CSS chunk/i,
];

export function isStaleBundleError(message: string | undefined | null): boolean {
  if (!message) return false;
  return STALE_BUNDLE_PATTERNS.some((p) => p.test(message));
}

function reloadOnce(): void {
  // sessionStorage isn't available in some sandboxed iframes; guard it.
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return;
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    // If storage is unavailable we still reload — worst case is a single
    // extra reload, never an infinite loop because the failing chunk URL
    // either resolves or doesn't.
  }
  window.location.reload();
}

/**
 * Same self-heal, for errors React swallowed before the window ever saw them.
 * A rejected `lazy(() => import(...))` is caught by React and rethrown during
 * render, so it fires NEITHER 'error' NOR 'unhandledrejection' — only an
 * ErrorBoundary sees it. Without this the user just sat on "Algo salió mal".
 * Returns true if it was a stale-bundle error (reload scheduled).
 */
export function handleStaleBundleError(error: unknown): boolean {
  const message = typeof error === 'string'
    ? error
    : (error as { message?: string } | null)?.message;
  if (!isStaleBundleError(message)) return false;
  reloadOnce();
  return true;
}

export function installStaleBundleGuard(): void {
  // If we reached this code, index.html + the main bundle loaded fine.
  // Wait briefly to be sure we're past initial render, then clear the
  // prev-attempt flag so a *future* deploy mid-session can also self-heal.
  setTimeout(() => {
    try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
  }, 5000);

  window.addEventListener('error', (event) => {
    const message = event.error?.message ?? event.message;
    if (isStaleBundleError(message)) reloadOnce();
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    const message = typeof reason === 'string'
      ? reason
      : (reason as { message?: string } | null)?.message;
    if (isStaleBundleError(message)) reloadOnce();
  });
}
